import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Principal } from '@icp-sdk/core/principal';
import { useAuth } from './auth';
import { explain } from './api';
import { traderService, traderResult, traderNotice, type TraderNotice } from './traders';
import { browserAlertsEnabled, browserAlertsSupported, closeBrowserTradeAlerts, disableBrowserTradeAlerts, enableBrowserTradeAlerts, showBrowserTradeAlerts } from './browser-trade-alerts';

interface Saved { owner: string; following: Set<string>; notices: TraderNotice[]; unread: number; retained: number; limit: number }
interface TraderState {
  following: Set<string>; pending: Set<string>; loading: boolean; error: string; notifications: TraderNotice[]; unread: number; hasMore: boolean; loadingMore: boolean;
  toggleFollow: (owner: string) => Promise<void>; markRead: () => Promise<void>; loadMore: () => Promise<void>; refresh: () => void;
  browserEnabled: boolean; browserSupported: boolean; browserBusy: boolean; toggleBrowser: () => Promise<void>;
}
const Context = createContext<TraderState | null>(null);
const mergeNotices = (first: TraderNotice[], rest: TraderNotice[], limit: number) => [...new Map([...rest, ...first].map(item => [item.id, item])).values()].sort((a, b) => a.id > b.id ? -1 : 1).slice(0, limit);
export function TraderProvider({ children }: { children: ReactNode }) {
  const auth = useAuth(), owner = auth.owner;
  const currentOwner = useRef(owner); currentOwner.current = owner;
  const [saved, setSaved] = useState<Saved | null>(null);
  const mutations = useRef(0), pollNow = useRef<(() => Promise<void>) | undefined>(undefined);
  const [pending, setPending] = useState(new Set<string>()), inflight = useRef(new Set<string>());
  const [loading, setLoading] = useState(false), [error, setError] = useState(''), [loadingMore, setLoadingMore] = useState(false);
  const [browserEnabled, setBrowserEnabled] = useState(false), [browserBusy, setBrowserBusy] = useState(false);
  const state = saved?.owner === owner ? saved : null;
  const refresh = () => { void pollNow.current?.(); };
  useEffect(() => {
    let active = true, running = false, queued = false, first = true, lastNotice: bigint | undefined;
    setSaved(null); setError(''); setLoading(Boolean(owner)); setPending(new Set()); inflight.current.clear(); setLoadingMore(false); setBrowserBusy(false);
    setBrowserEnabled(Boolean(owner && browserAlertsEnabled(owner)));
    if (!owner) return;
    const captured = owner;
    const isCurrent = () => active && currentOwner.current === captured;
    const poll = async () => {
      if (!isCurrent()) return;
      if (running) { queued = true; return; }
      running = true;
      const epoch = mutations.current;
      try {
        const identity = await auth.getIdentity();
        if (!isCurrent() || identity.getPrincipal().toText() !== captured) return;
        const service = await traderService(identity);
        const [following, response] = await Promise.all([service.follow_list(), service.notification_feed([], 100n)]);
        const follows = traderResult(following), feed = traderResult(response);
        const notices = feed.items.map(traderNotice), fresh = notices.filter(notice => lastNotice === undefined || notice.id > lastNotice);
        let cursor = feed.next_before[0];
        // Catch up a busy feed before updating the high-water mark. No old inbox
        // items are emitted to the OS on the first read of a session.
        if (!first) {
          while (cursor !== undefined && notices.length < Number(feed.retention_limit) && notices.length && (lastNotice === undefined || notices[notices.length - 1].id > lastNotice)) {
            const older = traderResult(await service.notification_feed([cursor], 100n));
            if (!older.items.length) break;
            const rows = older.items.map(traderNotice); notices.push(...rows); fresh.push(...rows.filter(notice => lastNotice === undefined || notice.id > lastNotice)); cursor = older.next_before[0];
          }
        }
        if (!isCurrent()) return;
        if (epoch !== mutations.current) { queued = true; return; }
        setSaved(previous => ({ owner: captured, following: new Set(follows.map(follow => follow.who.toText())), notices: mergeNotices(notices, previous?.owner === captured ? previous.notices : [], Number(feed.retention_limit)).map(notice => feed.unread_count === 0n ? { ...notice, read: true } : notice), unread: Number(feed.unread_count), retained: Number(feed.retained_count), limit: Number(feed.retention_limit) }));
        setError(''); setLoading(false); setBrowserEnabled(browserAlertsEnabled(captured));
        const shouldAlert = !first;
        first = false;
        if (notices.length) lastNotice = notices.reduce((latest, notice) => notice.id > latest ? notice.id : latest, lastNotice ?? 0n);
        if (shouldAlert && fresh.length) await showBrowserTradeAlerts(captured, fresh, isCurrent);
      } catch (reason) { if (isCurrent()) { setError(explain(reason)); setLoading(false); } }
      finally { running = false; if (queued && isCurrent()) { queued = false; void poll(); } }
    };
    pollNow.current = poll; void poll();
    const timer = setInterval(() => void poll(), 15_000);
    const visibility = () => { if (document.visibilityState === 'visible') { setBrowserEnabled(browserAlertsEnabled(captured)); void poll(); } };
    document.addEventListener('visibilitychange', visibility);
    return () => { active = false; pollNow.current = undefined; clearInterval(timer); document.removeEventListener('visibilitychange', visibility); void closeBrowserTradeAlerts(captured).catch(() => undefined); };
  }, [owner]);

  async function toggleFollow(target: string) {
    if (!owner) { await auth.login(); return; }
    if (!state || loading || inflight.current.has(target)) return;
    const captured = owner, add = !state.following.has(target);
    mutations.current++;
    inflight.current.add(target); setPending(new Set(inflight.current)); setError('');
    try {
      const identity = await auth.getIdentity();
      if (currentOwner.current !== captured) return;
      traderResult(await (await traderService(identity)).set_follow(Principal.fromText(target), add));
      if (currentOwner.current === captured) setSaved(previous => {
        if (previous?.owner !== captured) return previous;
        const following = new Set(previous.following); if (add) following.add(target); else following.delete(target);
        return { ...previous, following };
      });
    } catch (reason) { if (currentOwner.current === captured) setError(explain(reason)); }
    finally { if (currentOwner.current === captured) { mutations.current++; inflight.current.delete(target); setPending(new Set(inflight.current)); void pollNow.current?.(); } }
  }
  async function markRead() {
    if (!owner || !state?.notices.length) return;
    const captured = owner, upto = state.notices[0].id;
    mutations.current++;
    try {
      const identity = await auth.getIdentity(); if (currentOwner.current !== captured) return;
      traderResult(await (await traderService(identity)).mark_notifications_read(upto));
      if (currentOwner.current === captured) setSaved(previous => previous?.owner === captured ? { ...previous, notices: previous.notices.map(notice => notice.id <= upto ? { ...notice, read: true } : notice), unread: previous.notices.filter(notice => !notice.read && notice.id > upto).length } : previous);
    } catch (reason) { if (currentOwner.current === captured) setError(explain(reason)); }
    finally { if (currentOwner.current === captured) { mutations.current++; void pollNow.current?.(); } }
  }
  async function loadMore() {
    if (!owner || !state?.notices.length || loadingMore) return;
    const captured = owner, before = state.notices[state.notices.length - 1].id, epoch = mutations.current; setLoadingMore(true);
    try {
      const identity = await auth.getIdentity(); if (currentOwner.current !== captured) return;
      const feed = traderResult(await (await traderService(identity)).notification_feed([before], 100n));
      if (currentOwner.current === captured && mutations.current === epoch) setSaved(previous => previous?.owner === captured ? { ...previous, notices: mergeNotices(feed.items.map(traderNotice), previous.notices, Number(feed.retention_limit)), retained: Number(feed.retained_count), unread: Number(feed.unread_count) } : previous);
    } catch (reason) { if (currentOwner.current === captured) setError(explain(reason)); }
    finally { if (currentOwner.current === captured) setLoadingMore(false); }
  }
  async function toggleBrowser() {
    if (!owner) { await auth.login(); return; }
    const captured = owner; setBrowserBusy(true); setError('');
    try {
      if (browserEnabled) await disableBrowserTradeAlerts(captured); else await enableBrowserTradeAlerts(captured);
      if (currentOwner.current === captured) setBrowserEnabled(browserAlertsEnabled(captured));
      else await disableBrowserTradeAlerts(captured);
    } catch (reason) { if (currentOwner.current === captured) setError(explain(reason)); }
    finally { if (currentOwner.current === captured) setBrowserBusy(false); }
  }
  return <Context.Provider value={{ following: state?.following ?? new Set(), pending, loading, error, notifications: state?.notices ?? [], unread: state?.unread ?? 0, hasMore: Boolean(state && state.notices.length < state.retained), loadingMore, toggleFollow, markRead, loadMore, refresh, browserEnabled, browserSupported: browserAlertsSupported(), browserBusy, toggleBrowser }}>{children}</Context.Provider>;
}
export function useTraders() { const value = useContext(Context); if (!value) throw Error('TraderProvider is missing'); return value; }
